import React, { useState } from 'react';
import { loadAuthToken } from '@/data/utils/axiosConfigInstance';

const SyncStudioViews = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [usersLog, setUsersLog] = useState([]);
  const [summary, setSummary] = useState({ totalUsers: 0, completed: 0, errors: 0 });
  const [fetchStatus, setFetchStatus] = useState(''); // 'idle', 'fetching', 'error'

  const startSync = async () => {
    setIsSyncing(true);
    setUsersLog([]);
    setSummary({ totalUsers: 0, completed: 0, errors: 0 });
    setFetchStatus('fetching');

    const token = loadAuthToken();
    const baseUrl = import.meta.env.VITE_APP_API_URL || '';

    try {
      // 1. Fetch the list of users
      const usersResponse = await fetch(`${baseUrl}/mux/sync/studio-users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!usersResponse.ok) {
        throw new Error(`Failed to fetch users (HTTP ${usersResponse.status})`);
      }

      const usersResult = await usersResponse.json();
      if (!usersResult?.data) {
        throw new Error('Invalid response from server');
      }

      const users = usersResult.data;
      setSummary(prev => ({ ...prev, totalUsers: users.length }));

      // Initialize the log with all users set to Pending
      const initialLogs = users.map(u => ({
        id: u._id,
        email: u.email,
        userType: u.userType,
        fetched: 0,
        saved: 0,
        skipped: 0,
        status: 'Pending'
      }));
      setUsersLog(initialLogs);
      setFetchStatus('idle');

      // 2. Loop through users and sync them one by one
      for (const user of users) {
        // Mark current user as Syncing
        setUsersLog(prev => prev.map(log =>
          log.id === user._id ? { ...log, status: 'Syncing...' } : log
        ));

        try {
          const syncResponse = await fetch(`${baseUrl}/mux/sync/user-views`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ email: user.email, userId: user._id })
          });

          if (!syncResponse.ok) {
            throw new Error(`Sync failed (HTTP ${syncResponse.status})`);
          }

          const syncResult = await syncResponse.json();
          const data = syncResult.data;

          // Update log with success
          setUsersLog(prev => prev.map(log =>
            log.id === user._id ? {
              ...log,
              fetched: data.fetched,
              saved: data.saved,
              skipped: data.skipped,
              status: 'Success'
            } : log
          ));
          setSummary(prev => ({ ...prev, completed: prev.completed + 1 }));

        } catch (syncError) {
          console.error(`Failed to sync ${user.email}:`, syncError);
          setUsersLog(prev => prev.map(log =>
            log.id === user._id ? { ...log, status: 'Failed' } : log
          ));
          setSummary(prev => ({ ...prev, completed: prev.completed + 1, errors: prev.errors + 1 }));
        }

        // Small delay between users to avoid hammering the server/Mux API
        await new Promise(resolve => setTimeout(resolve, 500));
      }

    } catch (error) {
      console.error('Error during sync setup:', error);
      setFetchStatus('error');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h4 className="m-0" style={{ color: "white" }}>Sync Studio Owner & Sub-user Views</h4>
          <button
            className="btn btn-light fw-bold"
            onClick={startSync}
            disabled={isSyncing}
          >
            {isSyncing ? 'Syncing...' : 'Start Sync'}
          </button>
        </div>
        <div className="card-body">
          <p className="text-muted">
            This tool fetches the latest 90 days of video views from Mux for all Studio Owners and Sub-users and saves them to the database.
          </p>

          {fetchStatus === 'error' && (
            <div className="alert alert-danger">
              Failed to connect to the server. Please check your network or try again later.
            </div>
          )}

          {summary.totalUsers > 0 && (
            <div className="alert alert-info d-flex justify-content-between align-items-center">
              <span>
                <strong>Progress: </strong> {summary.completed} / {summary.totalUsers} users processed.
              </span>
              {summary.completed === summary.totalUsers && (
                <span className="badge bg-success fs-6">Complete!</span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header bg-light">
          <h5 className="m-0">Sync Progress Log</h5>
        </div>
        <div className="card-body p-0" style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <table className="table table-hover table-striped m-0">
            <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th>Email</th>
                <th>User Type</th>
                <th>Views Fetched</th>
                <th>Views Saved</th>
                <th>Views Skipped (Dupes)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {usersLog.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center p-4 text-muted">
                    {fetchStatus === 'fetching' ? 'Fetching list of users from the database...' : 'Click "Start Sync" to begin the process.'}
                  </td>
                </tr>
              )}
              {usersLog.map((log) => (
                <tr key={log.id}>
                  <td>{log.email}</td>
                  <td className="text-capitalize">{log.userType}</td>
                  <td>{log.fetched}</td>
                  <td className="text-success">{log.saved}</td>
                  <td className="text-warning">{log.skipped}</td>
                  <td>
                    <span className={`badge ${log.status === 'Success' ? 'bg-success' : log.status === 'Syncing...' ? 'bg-info' : log.status === 'Pending' ? 'bg-secondary' : 'bg-danger'}`}>
                      {log.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SyncStudioViews;
